import {
  Button,
  Flex,
  FormControl,
  FormLabel,
  Input,
  Switch,
  Text,
  useToast,
} from '@chakra-ui/react';
import {
  Contact,
  ContactAddToType,
  contactsApi,
} from '@texas/api/endpoints/contactsApi';
import { Role, UserType, userApi } from '@texas/api/endpoints/userApi';
import { useApiRequest } from '@texas/api/hooks/useApiRequest';
import { ErrorDetails } from '@texas/components/shared/alert/ErrorDetails';
import { FeatureGuard } from '@texas/components/shared/featureGuard/FeatureGuard';
import { contactEvents } from '@texas/components/contacts/events';
import { useActiveSession } from '@texas/hooks/useSession';
import { hasRole } from '@texas/utils/helpers/roleHelpers';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

interface FormValues {
  name: string;
  email: string;
  createLogin: boolean;
}

export function NewSupplierContactForm({
  supplierId,
  onCancel,
  onCreated,
}: {
  supplierId: number;
  onCancel: () => void;
  onCreated: (contact: Contact) => void;
}) {
  const { t } = useTranslation();
  const toast = useToast();
  const session = useActiveSession();
  const canCreateLogin = hasRole(session.currentUser.role, [
    Role.SystemAdministrator,
  ]);
  const { request: createContact, error: contactError } = useApiRequest(
    contactsApi.create,
  );
  const { request: createUser, error: userError } = useApiRequest(
    userApi.createUser,
  );
  const {
    register,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({
    defaultValues: { name: '', email: '', createLogin: false },
  });

  const submit = async (values: FormValues) => {
    const response = await createContact({
      name: values.name.trim(),
      email: values.email.trim(),
      referenceId: supplierId,
      addTo: ContactAddToType.Supplier,
    });
    if (response.hasError) return;

    if (values.createLogin) {
      const userResponse = await createUser({
        name: values.name.trim(),
        email: values.email.trim(),
        role: Role.Supplier,
        userType: UserType.Supplier,
        providedPassword: null,
        emailNotifications: true,
        sendWelcomeMail: false,
        timeZone: null,
        betaFeatures: false,
        contactId: response.data.id,
        passwordLoginEnabled: true,
        rudholmIdentityLoginEnabled: false,
      });
      if (userResponse.hasError) {
        toast({
          title: t('variant.supplier.addModal.loginCreateFailed'),
          description: userResponse.error.message,
          status: 'error',
          isClosable: true,
        });
      }
    }

    contactEvents.newContactCreated.dispatch();
    onCreated(response.data);
    reset();
  };

  const createLogin = watch('createLogin');

  return (
    <Flex
      as="form"
      onSubmit={handleSubmit(submit)}
      direction="column"
      gap={4}
      p={4}
      border="1px solid"
      borderColor="entityDirectory.border"
      bg="entityDirectory.raisedSurface"
    >
      <Text fontWeight="semibold">
        {t('variant.supplier.addModal.newContact')}
      </Text>
      {contactError && <ErrorDetails error={contactError} />}
      {userError && <ErrorDetails error={userError} />}
      <FormControl isRequired={true} isInvalid={!!errors.name}>
        <FormLabel fontSize="sm">{t('general.name')}</FormLabel>
        <Input
          variant="entityDirectory"
          borderRadius={0}
          {...register('name', { required: true })}
        />
      </FormControl>
      <FormControl isRequired={true} isInvalid={!!errors.email}>
        <FormLabel fontSize="sm">{t('general.email')}</FormLabel>
        <Input
          type="email"
          variant="entityDirectory"
          borderRadius={0}
          placeholder="name@company.com"
          {...register('email', { required: true })}
        />
      </FormControl>
      <FeatureGuard>
        <FormControl
          display="flex"
          alignItems="center"
          justifyContent="space-between"
        >
          <FormLabel mb={0} fontSize="sm">
            {t('variant.supplier.addModal.createLogin')}
          </FormLabel>
          <Switch
            isChecked={createLogin}
            isDisabled={!canCreateLogin}
            onChange={(event) => setValue('createLogin', event.target.checked)}
          />
        </FormControl>
        <Text color="entityDirectory.mutedText" fontSize="xs" mt={2}>
          {t('variant.supplier.addModal.noWelcomeEmailHint')}
        </Text>
      </FeatureGuard>
      <Text color="entityDirectory.mutedText" fontSize="xs">
        {t('variant.supplier.addModal.newContactHint')}
      </Text>
      <Flex justify="end" gap={2}>
        <Button
          type="button"
          variant="entityDirectoryMenu"
          size="sm"
          onClick={onCancel}
        >
          {t('general.cancel')}
        </Button>
        <Button
          type="submit"
          variant="entityDirectorySecondary"
          size="sm"
          isLoading={isSubmitting}
        >
          {t('variant.supplier.addModal.createContact')}
        </Button>
      </Flex>
    </Flex>
  );
}
